// src/ventes/DetailClient.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Card, Row, Col, Button, Badge, Table, Spinner, Alert,
    Nav, Form
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
    ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   HELPERS DATE
   ============================================================ */
const getDateOnly = (dateInput) => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

const isBeforeToday = (d) => {
    const x = getDateOnly(d);
    return x ? x < getDateOnly(new Date()) : false;
};

const isAfterToday = (d) => {
    const x = getDateOnly(d);
    return x ? x > getDateOnly(new Date()) : false;
};

const isToday = (d) => {
    const x = getDateOnly(d);
    const t = getDateOnly(new Date());
    return x && t ? x.getTime() === t.getTime() : false;
};

/* ============================================================
   TOAST
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailClient() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [client, setClient] = useState(null);
    const [contrats, setContrats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingContrats, setLoadingContrats] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('employes');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');
    const [filterType, setFilterType] = useState('all');
    const [toast, setToast] = useState({ show: false, type: 'info', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'info', title: '', message: '' });

    const showSuccess = (msg) => showToast('success', 'Succès', msg);
    const showError   = (msg) => showToast('error', 'Erreur', msg);
    const showWarning = (msg) => showToast('warning', 'Attention', msg);
    const showInfo    = (msg) => showToast('info', 'Information', msg);

    useEffect(() => {
        fetchClientDetails();
        fetchContratsByClient();
    }, [id]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setClient(result.data);
        } catch (err) {
            setError(err.message);
            showError("Erreur lors du chargement du client");
        } finally {
            setLoading(false);
        }
    };

    const fetchContratsByClient = async () => {
        try {
            setLoadingContrats(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/contrats.php?action=by_client&id_client=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement contrats");
            const result = await response.json();
            setContrats(result.data || []);
        } catch (err) {
            console.error("Erreur contrats:", err);
        } finally {
            setLoadingContrats(false);
        }
    };

    /* ============================================================
       STATISTIQUES CALCULÉES
       ============================================================ */
    const stats = useMemo(() => {
        const now = new Date();
        const stats = {
            total: contrats.length,
            actifs: 0,
            expires: 0,
            aVenir: 0,
            cdd: 0,
            cdi: 0,
            avenants: 0,
            certificats: 0,
            employesUniques: new Set(),
            postes: new Set(),
            sites: new Set(),
            expirantBientot: 0
        };

        contrats.forEach(c => {
            // Statut
            if (c.statut === 'termine') { /* pass */ }
            else if (c.statut === 'expire' || (c.date_fin && isBeforeToday(c.date_fin))) stats.expires++;
            else if (c.date_debut && isAfterToday(c.date_debut)) stats.aVenir++;
            else stats.actifs++;

            // Type
            if (c.type_contrat === 'CDD') stats.cdd++;
            else if (c.type_contrat === 'CDI') stats.cdi++;
            else if (c.type_contrat === 'AVENANT') stats.avenants++;
            else if (c.type_contrat === 'CERTIFICAT') stats.certificats++;

            // Employé unique
            if (c.employe_id) stats.employesUniques.add(c.employe_id);
            else if (c.employe_nom && c.employe_prenom) {
                stats.employesUniques.add(`${c.employe_prenom}_${c.employe_nom}`);
            }

            // Postes / Sites
            if (c.poste_qualification) stats.postes.add(c.poste_qualification);
            if (c.poste_site) stats.sites.add(c.poste_site);

            // Expire bientôt
            if (c.statut === 'actif' && c.date_fin) {
                const d = getDateOnly(c.date_fin);
                const today = getDateOnly(now);
                const week = getDateOnly(now);
                week.setDate(week.getDate() + 7);
                if (d && d >= today && d <= week) stats.expirantBientot++;
            }
        });

        return {
            ...stats,
            employesUniques: stats.employesUniques.size,
            postes: stats.postes.size,
            sites: stats.sites.size
        };
    }, [contrats]);

    /* ============================================================
       FILTRES
       ============================================================ */
    const filteredContrats = useMemo(() => {
        return contrats.filter(c => {
            const searchMatch = !searchTerm ||
                (c.employe_nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.employe_prenom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.poste_qualification || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (c.poste_site || '').toLowerCase().includes(searchTerm.toLowerCase());

            let statutMatch = true;
            if (filterStatut === 'actif') statutMatch = c.statut === 'actif' && !isBeforeToday(c.date_fin);
            else if (filterStatut === 'expire') statutMatch = c.statut === 'expire' || (c.date_fin && isBeforeToday(c.date_fin));
            else if (filterStatut === 'avenir') statutMatch = c.date_debut && isAfterToday(c.date_debut);

            const typeMatch = filterType === 'all' || c.type_contrat === filterType;

            return searchMatch && statutMatch && typeMatch;
        });
    }, [contrats, searchTerm, filterStatut, filterType]);

    /* ============================================================
       HELPERS AFFICHAGE
       ============================================================ */
    const getStatutConfig = (c) => {
        if (c.statut === 'termine') return { gradient: 'linear-gradient(135deg, #64748b, #475569)', text: 'Terminé', icon: 'ri-checkbox-circle-line' };
        if (c.statut === 'expire' || (c.date_fin && isBeforeToday(c.date_fin))) return { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', text: 'Expiré', icon: 'ri-error-warning-line' };
        if (c.date_debut && isAfterToday(c.date_debut)) return { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', text: 'À venir', icon: 'ri-time-line' };
        return { gradient: 'linear-gradient(135deg, #10b981, #059669)', text: 'Actif', icon: 'ri-checkbox-circle-line' };
    };

    const getStatutBadge = (c) => {
        const cfg = getStatutConfig(c);
        return (
            <span className="status-badge-charte" style={{ background: cfg.gradient }}>
                <i className={cfg.icon}></i> {cfg.text}
            </span>
        );
    };

    const getTypeConfig = (c) => {
        const types = {
            'CDD': { gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)', label: 'CDD', icon: 'ri-file-chart-line' },
            'CDI': { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', label: 'CDI', icon: 'ri-file-text-line' },
            'AVENANT': { gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', label: 'Avenant', icon: 'ri-file-add-line' },
            'CERTIFICAT': { gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', label: 'Certificat', icon: 'ri-award-line' }
        };
        return types[c.type_contrat] || types['CDD'];
    };

    const getTypeBadge = (c) => {
        const cfg = getTypeConfig(c);
        return (
            <span className="status-badge-charte" style={{ background: cfg.gradient }}>
                <i className={cfg.icon}></i> {cfg.label}
            </span>
        );
    };

    /* ============================================================
       EXPORTS
       ============================================================ */
    const exporterEmployesPDF = () => {
        try {
            showInfo("Génération du PDF en cours...");
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

            // En-tête
            doc.setFillColor(41, 90, 167);
            doc.rect(0, 0, 297, 30, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.text("EMPLOYÉS AFFECTÉS", 148.5, 15, { align: 'center' });
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(client.raison_sociale, 148.5, 24, { align: 'center' });

            // Infos client
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(`Code client : ${client.code_client}`, 15, 40);
            doc.text(`Date d'édition : ${formatDate(new Date())}`, 250, 40, { align: 'right' });

            // Résumé
            doc.setFillColor(240, 244, 250);
            doc.rect(15, 45, 267, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.text(`Total contrats : ${stats.total}`, 20, 55);
            doc.text(`Contrats actifs : ${stats.actifs}`, 80, 55);
            doc.text(`Employés uniques : ${stats.employesUniques}`, 145, 55);
            doc.text(`Contrats expirés : ${stats.expires}`, 210, 55);

            // Tableau
            const rows = filteredContrats.map(c => [
                c.reference || `#${c.id}`,
                `${c.employe_prenom || ''} ${c.employe_nom || ''}`.trim() || '—',
                c.poste_qualification || '—',
                c.poste_site || '—',
                c.type_contrat || 'CDD',
                formatDate(c.date_debut),
                c.date_fin ? formatDate(c.date_fin) : 'CDI',
                getStatutConfig(c).text
            ]);

            autoTable(doc, {
                startY: 68,
                head: [["Référence", "Employé", "Poste", "Site", "Type", "Début", "Fin", "Statut"]],
                body: rows,
                theme: 'striped',
                styles: { fontSize: 8, cellPadding: 2.5 },
                headStyles: { fillColor: [41, 90, 167], textColor: 255, fontStyle: 'bold', halign: 'center' },
                alternateRowStyles: { fillColor: [248, 250, 252] }
            });

            doc.save(`employes_${client.code_client}_${Date.now()}.pdf`);
            showSuccess("PDF généré avec succès !");
        } catch (error) {
            console.error(error);
            showError("Erreur lors de la génération du PDF");
        }
    };

    const exporterEmployesExcel = () => {
        try {
            showInfo("Génération Excel en cours...");
            const dataExcel = filteredContrats.map(c => ({
                'Référence': c.reference || `#${c.id}`,
                'Nom employé': c.employe_nom || '',
                'Prénom employé': c.employe_prenom || '',
                'Email': c.employe_email || '',
                'Téléphone': c.employe_telephone || '',
                'Poste': c.poste_qualification || '',
                'Fonction': c.poste_fonction || '',
                'Département': c.poste_departement || '',
                'Site': c.poste_site || '',
                'Province': c.poste_province || '',
                'Type contrat': c.type_contrat || 'CDD',
                'Date début': formatDate(c.date_debut),
                'Date fin': c.date_fin ? formatDate(c.date_fin) : 'CDI',
                'Statut': getStatutConfig(c).text
            }));

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(dataExcel);
            ws['!cols'] = [
                { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
                { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
                { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }
            ];
            XLSX.utils.book_append_sheet(wb, ws, "Employés");
            XLSX.writeFile(wb, `employes_${client.code_client}_${Date.now()}.xlsx`);
            showSuccess("Excel généré avec succès !");
        } catch (error) {
            showError("Erreur export Excel : " + error.message);
        }
    };

    /* ============================================================
       DONNÉES GRAPHIQUES
       ============================================================ */
    const contratsParMois = useMemo(() => {
        const map = {};
        contrats.forEach(c => {
            if (!c.date_debut) return;
            const d = new Date(c.date_debut);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map)
            .sort()
            .slice(-6)
            .map(([k, v]) => {
                const [year, month] = k.split('-');
                const mois = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
                return { mois: `${mois[parseInt(month) - 1]} ${year.slice(2)}`, contrats: v };
            });
    }, [contrats]);

    const pieData = [
        { name: 'Actifs', value: stats.actifs },
        { name: 'Expirés', value: stats.expires },
        { name: 'À venir', value: stats.aVenir }
    ].filter(d => d.value > 0);

    const PIE_COLORS = [COLORS.success || '#10b981', COLORS.danger || '#ef4444', COLORS.warning || '#f59e0b'];

    /* ============================================================
       LOADING
       ============================================================ */
    if (loading) {
          return (
                    <>
                        <Header />
                        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                                <div className="text-center" style={{ maxWidth: '400px' }}>
                                    <div className="loading-ring-wrapper mb-4">
                                        <div className="loading-ring"></div>
                                        <div className="loading-ring-inner"></div>
                                        <i className="ri-loader-4-fill loading-icon"></i>
                                    </div>
                                    <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                        Chargement de la page...
                                    </h5>
                                    <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                                    <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                        <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                            <div
                                                className="progress-bar progress-bar-striped progress-bar-animated"
                                                style={{
                                                    width: '100%',
                                                    background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                                    backgroundSize: '200% 100%',
                                                    animation: 'shimmer 1.5s linear infinite'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <Footer />
                        <style>{`
                            .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                            .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                            .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                            .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                            @keyframes spin { to { transform: rotate(360deg); } }
                            @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                            @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                        `}</style>
                    </>
                );
    }

    if (error || !client) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon"><i className="ri-error-warning-line"></i></div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Client non trouvé"}</p>
                            </div>
                            <Button onClick={() => navigate('/ventes/clients/liste')} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-user-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">{client.raison_sociale}</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i> {client.code_client}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i> Client depuis {formatDate(client.date_creation)}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-file-copy-line"></i> {stats.total} contrat(s)
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={() => navigate('/ventes/clients/liste')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                            <Button className="btn-modern-primary" as={Link} to={`/rh/contratsNouveau?id_client=${client.id_client}`}>
                                <i className="ri-add-line me-1"></i> Nouveau contrat
                            </Button>
                            <Button className="btn-modern-secondary" as={Link} to={`/ventes/clients/modifier/${client.id_client}`}>
                                <i className="ri-pencil-line me-1"></i> Modifier
                            </Button>
                        </div>
                    </div>

                    {/* ===== CARTE PROFIL ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 profile-card-charte">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-4">
                                <Col md={8}>
                                    <div className="d-flex align-items-center gap-4">
                                        <div className="client-avatar-charte">
                                            {client.raison_sociale?.charAt(0)?.toUpperCase() || 'C'}
                                        </div>
                                        <div>
                                            <h3 className="fw-bold mb-2" style={{ color: COLORS.primaryDark }}>
                                                {client.raison_sociale}
                                            </h3>
                                            <div className="d-flex flex-wrap gap-3">
                                                <span className="info-chip-charte">
                                                    <i className="ri-phone-line"></i>
                                                    {client.telephone || 'Non renseigné'}
                                                </span>
                                                <span className="info-chip-charte">
                                                    <i className="ri-mail-line"></i>
                                                    {client.email || 'Non renseigné'}
                                                </span>
                                                <span className="info-chip-charte">
                                                    <i className="ri-map-pin-line"></i>
                                                    {client.adresse || 'Non renseignée'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="profile-stats-charte">
                                        <div className="profile-stat-item-charte">
                                            <span className="profile-stat-label">Contrats actifs</span>
                                            <span className="profile-stat-value" style={{ color: COLORS.success }}>
                                                {stats.actifs}
                                            </span>
                                        </div>
                                        <div className="profile-stat-item-charte">
                                            <span className="profile-stat-label">Employés affectés</span>
                                            <span className="profile-stat-value" style={{ color: COLORS.primary }}>
                                                {stats.employesUniques}
                                            </span>
                                        </div>
                                        <div className="profile-stat-item-charte">
                                            <span className="profile-stat-label">Sites différents</span>
                                            <span className="profile-stat-value" style={{ color: COLORS.info }}>
                                                {stats.sites}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== KPI ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-primary">
                                <div className="kpi-icon-charte"><i className="ri-file-copy-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Total contrats</span>
                                    <h3 className="kpi-value-charte">{stats.total}</h3>
                                    <span className="kpi-unit-charte">{stats.cdd} CDD • {stats.cdi} CDI</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-success">
                                <div className="kpi-icon-charte"><i className="ri-user-star-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Employés affectés</span>
                                    <h3 className="kpi-value-charte">{stats.employesUniques}</h3>
                                    <span className="kpi-unit-charte">personnes différentes</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-danger">
                                <div className="kpi-icon-charte"><i className="ri-error-warning-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Contrats expirés</span>
                                    <h3 className="kpi-value-charte">{stats.expires}</h3>
                                    <span className="kpi-unit-charte">
                                        {stats.expirantBientot > 0 ? `dont ${stats.expirantBientot} bientôt` : 'aucun bientôt'}
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-charte kpi-info">
                                <div className="kpi-icon-charte"><i className="ri-map-pin-line"></i></div>
                                <div className="kpi-content-charte">
                                    <span className="kpi-label-charte">Sites d'affectation</span>
                                    <h3 className="kpi-value-charte">{stats.sites}</h3>
                                    <span className="kpi-unit-charte">{stats.postes} poste(s) différent(s)</span>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== TABS ===== */}
                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="card-tabs-header-charte">
                            <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="tabs-charte">
                                <Nav.Item>
                                    <Nav.Link eventKey="employes">
                                        <span className="tab-title-charte">
                                            <i className="ri-team-line"></i>Employés affectés
                                        </span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="informations">
                                        <span className="tab-title-charte">
                                            <i className="ri-information-line"></i>Informations
                                        </span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="statistiques">
                                        <span className="tab-title-charte">
                                            <i className="ri-bar-chart-line"></i>Statistiques
                                        </span>
                                    </Nav.Link>
                                </Nav.Item>
                                <Nav.Item>
                                    <Nav.Link eventKey="documents">
                                        <span className="tab-title-charte">
                                            <i className="ri-folder-line"></i>Documents
                                        </span>
                                    </Nav.Link>
                                </Nav.Item>
                            </Nav>
                        </Card.Header>
                        <Card.Body className="p-4">

                            {/* === ONGLET EMPLOYÉS === */}
                            {activeTab === 'employes' && (
                                <div>
                                    <div className="section-title-charte mb-3">
                                        <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-team-line"></i>
                                        </span>
                                        <h6 className="mb-0">Employés affectés à ce client</h6>
                                        <Badge bg="primary" className="ms-auto">{filteredContrats.length}</Badge>
                                    </div>

                                    {/* Filtres */}
                                    <Row className="g-2 mb-3">
                                        <Col md={5}>
                                            <div className="search-box-charte-light">
                                                <i className="ri-search-line"></i>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Rechercher un employé, poste, site..."
                                                    value={searchTerm}
                                                    onChange={e => setSearchTerm(e.target.value)}
                                                    className="search-input-light"
                                                />
                                            </div>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Select value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                                                <option value="all">Tous les statuts</option>
                                                <option value="actif">Actifs</option>
                                                <option value="expire">Expirés</option>
                                                <option value="avenir">À venir</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Select value={filterType} onChange={e => setFilterType(e.target.value)}>
                                                <option value="all">Tous types</option>
                                                <option value="CDD">CDD</option>
                                                <option value="CDI">CDI</option>
                                                <option value="AVENANT">Avenant</option>
                                                <option value="CERTIFICAT">Certificat</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md={2} className="text-end">
                                            <Button className="btn-modern-outline w-100" onClick={() => { setSearchTerm(''); setFilterStatut('all'); setFilterType('all'); }}>
                                                <i className="ri-refresh-line"></i> Reset
                                            </Button>
                                        </Col>
                                    </Row>

                                    {loadingContrats ? (
                                        <div className="text-center py-5">
                                            <Spinner animation="border" style={{ color: COLORS.primary }} />
                                            <p className="text-muted small mt-3">Chargement des employés...</p>
                                        </div>
                                    ) : filteredContrats.length > 0 ? (
                                        <div className="table-responsive">
                                            <Table hover className="align-middle mb-0 table-charte">
                                                <thead>
                                                    <tr>
                                                        <th>Employé</th>
                                                        <th>Poste</th>
                                                        <th>Site</th>
                                                        <th className="text-center">Type</th>
                                                        <th>Période</th>
                                                        <th className="text-center">Statut</th>
                                                        <th className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredContrats.map(c => (
                                                        <tr key={c.id}>
                                                            <td>
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="employee-avatar-charte">
                                                                        {(c.employe_prenom || '?').charAt(0)}{(c.employe_nom || '?').charAt(0)}
                                                                    </div>
                                                                    <div>
                                                                        <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>
                                                                            {c.employe_prenom} {c.employe_nom}
                                                                        </div>
                                                                        {c.employe_email && (
                                                                            <small className="text-muted">
                                                                                <i className="ri-mail-line me-1" style={{ fontSize: '0.7rem' }}></i>
                                                                                {c.employe_email}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="text-muted small">
                                                                {c.poste_qualification || '—'}
                                                                {c.poste_departement && (
                                                                    <>
                                                                        <br />
                                                                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                                            <i className="ri-node-tree me-1"></i>
                                                                            {c.poste_departement}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {c.poste_site ? (
                                                                    <span className="info-chip-small">
                                                                        <i className="ri-map-pin-line"></i>
                                                                        {c.poste_site}
                                                                    </span>
                                                                ) : <span className="text-muted">—</span>}
                                                            </td>
                                                            <td className="text-center">{getTypeBadge(c)}</td>
                                                            <td className="small">
                                                                <div><i className="ri-calendar-check-line text-success me-1"></i>{formatDate(c.date_debut)}</div>
                                                                <div>
                                                                    <i className={`ri-calendar-close-line me-1 ${c.date_fin && isBeforeToday(c.date_fin) ? 'text-danger' : 'text-muted'}`}></i>
                                                                    {c.date_fin ? formatDate(c.date_fin) : <span className="text-muted">CDI</span>}
                                                                </div>
                                                            </td>
                                                            <td className="text-center">{getStatutBadge(c)}</td>
                                                            <td className="text-center">
                                                                <Button
                                                                    size="sm"
                                                                    className="btn-action-icon-charte"
                                                                    onClick={() => navigate(`/rh/contrats/${c.id}`)}
                                                                    title="Voir le contrat"
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte"><i className="ri-team-line"></i></div>
                                            <h6 className="fw-semibold mb-1">Aucun employé affecté</h6>
                                            <p className="text-muted small mb-3">
                                                {contrats.length === 0
                                                    ? "Ce client n'a encore aucun contrat"
                                                    : "Aucun résultat avec ces filtres"}
                                            </p>
                                            <Button className="btn-modern-primary" as={Link} to={`/rh/contratsNouveau?id_client=${client.id_client}`}>
                                                <i className="ri-add-line me-1"></i> Créer un contrat
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* === ONGLET INFORMATIONS === */}
                            {activeTab === 'informations' && (
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-contacts-line"></i>
                                            </span>
                                            <h6 className="mb-0">Coordonnées</h6>
                                        </div>
                                        <div className="info-card-charte">
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-phone-line"></i>Téléphone</span>
                                                <span className="info-value-charte">{client.telephone || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-mail-line"></i>Email</span>
                                                <span className="info-value-charte">{client.email || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-map-pin-line"></i>Adresse</span>
                                                <span className="info-value-charte">{client.adresse || '—'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-briefcase-line"></i>
                                            </span>
                                            <h6 className="mb-0">Identifiants juridiques</h6>
                                        </div>
                                        <div className="info-card-charte">
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-bank-card-line"></i>Numéro compte</span>
                                                <span className="info-value-charte">{client.numero_compte || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-fingerprint-line"></i>ICE</span>
                                                <span className="info-value-charte">{client.ice || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-file-list-line"></i>Patente</span>
                                                <span className="info-value-charte">{client.patente || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-book-2-line"></i>RC</span>
                                                <span className="info-value-charte">{client.rc || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-receipt-line"></i>Identifiant fiscal</span>
                                                <span className="info-value-charte">{client.identifiant_fiscal || '—'}</span>
                                            </div>
                                            <div className="info-row-charte">
                                                <span className="info-label-charte"><i className="ri-shield-user-line"></i>CNSS</span>
                                                <span className="info-value-charte">{client.cnss || '—'}</span>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* === ONGLET STATISTIQUES === */}
                            {activeTab === 'statistiques' && (
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                <i className="ri-line-chart-line"></i>
                                            </span>
                                            <h6 className="mb-0">Évolution des contrats</h6>
                                        </div>
                                        <div className="chart-container-charte">
                                            {contratsParMois.length > 0 ? (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <AreaChart data={contratsParMois}>
                                                        <defs>
                                                            <linearGradient id="colorContrats" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.4} />
                                                                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                        <XAxis dataKey="mois" tick={{ fontSize: 11, fill: '#64748b' }} />
                                                        <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                                                        <RechartsTooltip />
                                                        <Area type="monotone" dataKey="contrats" stroke={COLORS.primary} fillOpacity={1} fill="url(#colorContrats)" />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte"><i className="ri-line-chart-line"></i></div>
                                                    <p className="text-muted small mb-0">Aucune donnée disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                                <i className="ri-pie-chart-line"></i>
                                            </span>
                                            <h6 className="mb-0">Répartition par statut</h6>
                                        </div>
                                        <div className="chart-container-charte">
                                            {pieData.length > 0 ? (
                                                <ResponsiveContainer width="100%" height={260}>
                                                    <PieChart>
                                                        <Pie
                                                            data={pieData} cx="50%" cy="50%"
                                                            innerRadius={60} outerRadius={90}
                                                            paddingAngle={5} dataKey="value"
                                                        >
                                                            {pieData.map((entry, index) => (
                                                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip />
                                                        <Legend />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <div className="empty-state-charte">
                                                    <div className="empty-state-icon-charte"><i className="ri-pie-chart-line"></i></div>
                                                    <p className="text-muted small mb-0">Aucune donnée disponible</p>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div className="section-title-charte">
                                            <span className="section-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                <i className="ri-bar-chart-box-line"></i>
                                            </span>
                                            <h6 className="mb-0">Indicateurs</h6>
                                        </div>
                                        <Row className="g-3">
                                            <Col md={3}>
                                                <div className="indicator-charte">
                                                    <div className="indicator-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                                        <i className="ri-file-copy-line"></i>
                                                    </div>
                                                    <div className="indicator-label-charte">Total contrats</div>
                                                    <h5 className="indicator-value-charte" style={{ color: COLORS.primary }}>
                                                        {stats.total}
                                                    </h5>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="indicator-charte">
                                                    <div className="indicator-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                        <i className="ri-user-star-line"></i>
                                                    </div>
                                                    <div className="indicator-label-charte">Employés uniques</div>
                                                    <h5 className="indicator-value-charte" style={{ color: COLORS.success }}>
                                                        {stats.employesUniques}
                                                    </h5>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="indicator-charte">
                                                    <div className="indicator-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                                        <i className="ri-time-line"></i>
                                                    </div>
                                                    <div className="indicator-label-charte">Expirent bientôt</div>
                                                    <h5 className="indicator-value-charte" style={{ color: COLORS.warning }}>
                                                        {stats.expirantBientot}
                                                    </h5>
                                                </div>
                                            </Col>
                                            <Col md={3}>
                                                <div className="indicator-charte">
                                                    <div className="indicator-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                                        <i className="ri-file-add-line"></i>
                                                    </div>
                                                    <div className="indicator-label-charte">Avenants</div>
                                                    <h5 className="indicator-value-charte" style={{ color: COLORS.info }}>
                                                        {stats.avenants}
                                                    </h5>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            )}

                            {/* === ONGLET DOCUMENTS === */}
                            {activeTab === 'documents' && (
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="document-card-charte">
                                            <div className="document-icon-charte" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                                <i className="ri-file-pdf-line"></i>
                                            </div>
                                            <h5 className="document-title-charte">Liste des employés</h5>
                                            <p className="document-desc-charte">
                                                {client.raison_sociale} • {filteredContrats.length} contrat(s)
                                            </p>
                                            <Button className="btn-modern-danger" size="sm" onClick={exporterEmployesPDF}>
                                                <i className="ri-download-line me-2"></i>Télécharger PDF
                                            </Button>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="document-card-charte">
                                            <div className="document-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                                <i className="ri-file-excel-line"></i>
                                            </div>
                                            <h5 className="document-title-charte">Export Excel employés</h5>
                                            <p className="document-desc-charte">Détails complets des affectations</p>
                                            <Button className="btn-modern-success" size="sm" onClick={exporterEmployesExcel}>
                                                <i className="ri-download-line me-2"></i>Export Excel
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* ===== STYLES ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }
                .header-modern { background: white; padding: 1.2rem 1.8rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid var(--secondary); border-radius: 12px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--secondary)); display: flex; align-items: center; justify-content: center; border-radius: 14px; color: white; font-size: 1.5rem; box-shadow: 0 4px 12px rgba(41,90,167,0.25); }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern { background: #f1f4f9; color: #475569; padding: 0.3rem 0.8rem; border-radius: 50px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px; }
                .badge-modern i { color: var(--secondary); }
                .status-badge-charte { color: white; padding: 0.35rem 0.9rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; }
                .profile-card-charte { background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); }
                .client-avatar-charte { width: 88px; height: 88px; border-radius: 20px; background: linear-gradient(135deg, var(--primary), var(--primary-dark)); display: flex; align-items: center; justify-content: center; color: white; font-size: 2.2rem; font-weight: 800; box-shadow: 0 8px 24px rgba(41,90,167,0.3); flex-shrink: 0; }
                .info-chip-charte { display: inline-flex; align-items: center; gap: 6px; background: #f1f5f9; color: #475569; padding: 0.35rem 0.9rem; border-radius: 50px; font-size: 0.8rem; font-weight: 500; }
                .info-chip-charte i { color: var(--secondary); }
                .info-chip-small { display: inline-flex; align-items: center; gap: 4px; background: ${COLORS.secondary}10; color: var(--secondary); padding: 0.25rem 0.65rem; border-radius: 50px; font-size: 0.7rem; font-weight: 600; }
                .profile-stats-charte { background: white; padding: 1rem 1.25rem; border-radius: 12px; border: 1px solid #e2e8f0; }
                .profile-stat-item-charte { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0; border-bottom: 1px dashed #e2e8f0; }
                .profile-stat-item-charte:last-child { border-bottom: none; }
                .profile-stat-label { font-size: 0.8rem; color: #94a3b8; font-weight: 500; }
                .profile-stat-value { font-size: 0.95rem; font-weight: 700; color: #1e293b; }
                .kpi-charte { background: white; border-radius: 12px; padding: 1.2rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 2px 8px rgba(0,0,0,0.04); border-left: 4px solid; transition: all 0.25s ease; height: 100%; }
                .kpi-charte:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-icon-charte { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; flex-shrink: 0; }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value-charte { font-size: 1.35rem; font-weight: 700; margin: 0.3rem 0; color: #1e293b; }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }
                .card-tabs-header-charte { background: white; border: none; padding: 0; border-top-left-radius: 12px !important; border-top-right-radius: 12px !important; }
                .tabs-charte { border-bottom: 1px solid #e2e8f0; padding: 0 1rem; gap: 0.25rem; }
                .tabs-charte .nav-link { border: none; color: #64748b; padding: 1rem 1.25rem; font-weight: 500; font-size: 0.9rem; transition: all 0.2s ease; border-bottom: 3px solid transparent; background: transparent; }
                .tabs-charte .nav-link:hover { color: var(--primary); background: #f8fafc; border-bottom-color: #e2e8f0; }
                .tabs-charte .nav-link.active { color: var(--primary); border-bottom-color: var(--primary); background: transparent; }
                .tab-title-charte { display: inline-flex; align-items: center; gap: 8px; }
                .section-title-charte { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; padding-bottom: 0.5rem; border-bottom: 2px solid #f1f5f9; }
                .section-title-charte h6 { margin: 0; font-weight: 600; font-size: 0.95rem; color: #1e293b; }
                .section-icon-charte { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 1rem; }
                .search-box-charte-light { position: relative; }
                .search-box-charte-light > i { position: absolute; top: 50%; left: 12px; transform: translateY(-50%); color: var(--primary); z-index: 2; pointer-events: none; }
                .search-input-light { padding-left: 2.5rem !important; border-radius: 50px !important; border: 1.5px solid #e2e8f0 !important; }
                .search-input-light:focus { border-color: var(--secondary) !important; box-shadow: 0 0 0 3px ${COLORS.secondary}15 !important; }
                .info-card-charte { background: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0; padding: 0.5rem 1rem; }
                .info-row-charte { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px dashed #e2e8f0; }
                .info-row-charte:last-child { border-bottom: none; }
                .info-label-charte { display: inline-flex; align-items: center; gap: 8px; color: #64748b; font-size: 0.8rem; font-weight: 500; }
                .info-label-charte i { color: var(--secondary); font-size: 0.9rem; }
                .info-value-charte { font-weight: 600; color: #1e293b; font-size: 0.85rem; text-align: right; }
                .table-charte thead th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; padding: 0.9rem 0.75rem; }
                .table-charte tbody td { padding: 0.9rem 0.75rem; vertical-align: middle; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .employee-avatar-charte { width: 42px; height: 42px; border-radius: 12px; background: linear-gradient(135deg, ${COLORS.primary}15, ${COLORS.secondary}15); color: var(--primary); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 0.85rem; flex-shrink: 0; }
                .btn-action-icon-charte { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; background: white; border: 1.5px solid #e2e8f0; color: #64748b; font-size: 1rem; transition: all 0.2s ease; }
                .btn-action-icon-charte:hover { border-color: var(--secondary); color: var(--secondary); transform: translateY(-2px); box-shadow: 0 4px 12px rgba(33,172,205,0.15); }
                .chart-container-charte { background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 1rem; }
                .indicator-charte { background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 1.25rem; text-align: center; transition: all 0.25s ease; }
                .indicator-charte:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(0,0,0,0.06); border-color: var(--secondary); }
                .indicator-icon-charte { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin: 0 auto 0.75rem; font-size: 1.4rem; }
                .indicator-label-charte { font-size: 0.7rem; color: #94a3b8; text-transform: uppercase; font-weight: 600; letter-spacing: 0.4px; }
                .indicator-value-charte { font-size: 1.25rem; font-weight: 700; margin: 0.5rem 0 0; }
                .document-card-charte { background: white; border-radius: 12px; border: 1px solid #e2e8f0; padding: 1.5rem; text-align: center; transition: all 0.3s ease; height: 100%; display: flex; flex-direction: column; align-items: center; }
                .document-card-charte:hover { transform: translateY(-5px); box-shadow: 0 16px 40px rgba(0,0,0,0.08); border-color: var(--secondary); }
                .document-icon-charte { width: 72px; height: 72px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 2rem; margin-bottom: 1rem; }
                .document-title-charte { font-weight: 700; font-size: 1rem; color: #1e293b; margin-bottom: 0.5rem; }
                .document-desc-charte { font-size: 0.8rem; color: #94a3b8; margin-bottom: 1.25rem; }
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte { width: 72px; height: 72px; background: linear-gradient(135deg, #f1f5f9, #e2e8f0); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8; }
                .btn-modern-outline { border: 1.5px solid #d1d5db; background: white; padding: 0.5rem 1.4rem; border-radius: 50px; font-size: 0.85rem; font-weight: 500; color: #4b5563; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-outline:hover { border-color: var(--secondary); color: var(--secondary); background: #f0fbff; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(33,172,205,0.15); }
                .btn-modern-primary, .btn-modern-secondary, .btn-modern-success, .btn-modern-danger, .btn-modern-warning { border: none; color: white; padding: 0.5rem 1.5rem; border-radius: 50px; font-weight: 600; font-size: 0.85rem; transition: all 0.2s ease; display: inline-flex; align-items: center; gap: 6px; }
                .btn-modern-primary { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); box-shadow: 0 4px 12px rgba(41,90,167,0.3); }
                .btn-modern-primary:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white; }
                .btn-modern-secondary { background: linear-gradient(135deg, var(--secondary), #0d8ba6); box-shadow: 0 4px 12px rgba(33,172,205,0.3); }
                .btn-modern-secondary:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(33,172,205,0.4); color: white; }
                .btn-modern-success { background: linear-gradient(135deg, var(--success), #059669); box-shadow: 0 4px 12px rgba(16,185,129,0.3); }
                .btn-modern-success:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(16,185,129,0.4); color: white; }
                .btn-modern-danger { background: linear-gradient(135deg, var(--danger), #dc2626); box-shadow: 0 4px 12px rgba(239,68,68,0.3); }
                .btn-modern-danger:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(239,68,68,0.4); color: white; }
                .alert-error-charte { border: none; background: #fef2f2; border-left: 4px solid var(--danger); border-radius: 16px; padding: 1rem 1.5rem; box-shadow: 0 8px 32px rgba(239,68,68,0.08); }
                .alert-error-icon { width: 48px; height: 48px; border-radius: 12px; background: rgba(239,68,68,0.1); display: flex; align-items: center; justify-content: center; color: var(--danger); font-size: 1.5rem; }
                .loading-spinner-charte { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                .loading-ring-charte { position: absolute; inset: 0; border-radius: 50%; border: 4px solid #e2e8f0; border-top-color: var(--primary); animation: spin 1s linear infinite; }
                .loading-icon-charte { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: var(--primary); }
                .toast-notification-fixed { position: fixed; bottom: 24px; right: 24px; z-index: 9999; animation: slideInRight 0.3s ease-out; }
                .toast-modern { background: white; border-radius: 16px; box-shadow: 0 20px 40px rgba(0,0,0,0.15); min-width: 320px; max-width: 400px; overflow: hidden; }
                .toast-header-custom { padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
                .toast-close-btn { background: transparent; border: none; color: white; cursor: pointer; opacity: 0.8; }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
                @keyframes shrink { from { width: 100%; } to { width: 0%; } }
                @media (max-width: 992px) { .info-card-charte { margin-bottom: 1rem; } }
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .client-avatar-charte { width: 64px; height: 64px; font-size: 1.6rem; }
                    .profile-stats-charte { margin-top: 1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}